import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

type CourseItem = {
  id: number;
  category: string;
  name: string;
  instructor: string;
  department: string;
  assigned_user: string;
  status: "published" | "private";
  students: number;
  thumbnail?: string | null;
  video_url?: string | null;
  ppt_url?: string | null;
  pdf_url?: string | null;
  end_date?: string | null;
  category_id?: number | null;
};

type LoggedInUser = {
  id?: number;
  username?: string;
  email?: string;
  department?: string;
  role?: "employee" | "admin";
  profileImage?: string;
  profile_image?: string;
};

type CourseFilter = "all" | "assigned" | "optional" | "in_progress" | "completed";

type CourseProgressItem = {
  course_id: number;
  final_progress: number;
  course_completed: boolean;
};

type CourseCategoryGroup = {
  categoryKey: string;
  categoryName: string;
  courses: CourseItem[];
  thumbnailCourse: CourseItem;
  averageProgress: number;
  completedCount: number;
  inProgressCount: number;
};

type ChatUnreadCounts = {
  [senderId: string]: number;
};

function EmployeeCoursesPage() {
  const navigate = useNavigate();

  const [courses, setCourses] = useState<CourseItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [loggedInUser, setLoggedInUser] = useState<LoggedInUser | null>(null);
  const [employeeProfileImage, setEmployeeProfileImage] = useState("");
  const [courseSearchTerm, setCourseSearchTerm] = useState("");
  const [courseFilter, setCourseFilter] = useState<CourseFilter>("all");
  const [selectedCategoryGroup, setSelectedCategoryGroup] =
    useState<CourseCategoryGroup | null>(null);
  const [courseProgressMap, setCourseProgressMap] = useState<Record<number, CourseProgressItem>>({});
  const [chatUnreadCounts, setChatUnreadCounts] = useState<ChatUnreadCounts>({});

  useEffect(() => {
    const storedUser = sessionStorage.getItem("loggedInUser");

    if (storedUser) {
      const user: LoggedInUser = JSON.parse(storedUser);
      setLoggedInUser(user);

      const imagePath = user.profile_image || user.profileImage || "";

      setEmployeeProfileImage(
        imagePath && imagePath.startsWith("/uploads")
          ? `${imagePath}`
          : imagePath
      );

      if (user.id) {
        fetchChatUnreadCounts(user.id);
      }
    }

    fetchCourses();
  }, []);

  useEffect(() => {
    if (!loggedInUser?.id) return;

    const intervalId = window.setInterval(() => {
      fetchChatUnreadCounts(loggedInUser.id!);
    }, 3000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [loggedInUser?.id]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      fetchCourses(false);
    }, 5000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, []);

  const fetchCourses = async (showLoading: boolean = true) => {
    try {
      if (showLoading) {
        setLoading(true);
      }

      const response = await fetch("/courses/");

      if (!response.ok) {
        throw new Error("Failed to fetch courses");
      }

      const data = await response.json();
      setCourses(data);
    } catch (error) {
      console.error("Error fetching courses:", error);
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  const fetchChatUnreadCounts = async (userId: number) => {
    try {
      const response = await fetch(
        `/chat/unread-counts?user_id=${userId}`
      );

      if (!response.ok) {
        throw new Error("Failed to fetch chat unread counts");
      }

      const data = await response.json();
      setChatUnreadCounts(data);
    } catch (error) {
      console.error("Chat unread counts fetch error:", error);
    }
  };

  const fetchCourseProgress = async (targetCourses: CourseItem[], userId: number) => {
    try {
      const progressResults = await Promise.all(
        targetCourses.map(async (course) => {
          const response = await fetch(
            `/course-progress/${userId}/${course.id}`
          );

          if (!response.ok) {
            return null;
          }

          return await response.json();
        })
      );

      const progressMap: Record<number, CourseProgressItem> = {};

      progressResults.forEach((progress) => {
        if (progress) {
          progressMap[progress.course_id] = progress;
        }
      });

      setCourseProgressMap(progressMap);
    } catch (error) {
      console.error("Course progress fetch error:", error);
    }
  };

  const employeeCourses = useMemo(() => {
    const username = loggedInUser?.username?.trim().toLowerCase() || "";
    const email = loggedInUser?.email?.trim().toLowerCase() || "";
    const department = loggedInUser?.department?.trim().toLowerCase() || "";

    if (!username && !email && !department) {
      return [];
    }

    return courses.filter((course) => {
      const isPublished = course.status === "published";
      const assignedUserText = (course.assigned_user || "").trim().toLowerCase();
      const departmentText = (course.department || "").trim().toLowerCase();

      const matchesUser =
        (!!username && assignedUserText.includes(username)) ||
        (!!email && assignedUserText.includes(email));

      const matchesDepartment =
        !!department && departmentText.includes(department);

      return isPublished && (matchesUser || matchesDepartment);
    });
  }, [courses, loggedInUser]);

  useEffect(() => {
    if (loggedInUser?.id && employeeCourses.length > 0) {
      fetchCourseProgress(employeeCourses, loggedInUser.id);
    }
  }, [loggedInUser?.id, employeeCourses]);

  const filteredEmployeeCourses = useMemo(() => {
    const keyword = courseSearchTerm.trim().toLowerCase();

    let filteredCourses = employeeCourses;

    if (courseFilter === "optional") {
      filteredCourses = [];
    }

    if (courseFilter === "in_progress") {
      filteredCourses = employeeCourses.filter((course) => {
        const progress = courseProgressMap[course.id]?.final_progress || 0;
        return progress > 0 && progress < 100;
      });
    }

    if (courseFilter === "completed") {
      filteredCourses = employeeCourses.filter((course) => {
        const progress = courseProgressMap[course.id]?.final_progress || 0;
        return progress >= 100;
      });
    }

    if (!keyword) {
      return filteredCourses;
    }

    return filteredCourses.filter((course) => {
      const categoryName = course.category.toLowerCase();
      const courseName = course.name.toLowerCase();

      if (selectedCategoryGroup) {
        return courseName.includes(keyword);
      }

      return categoryName.includes(keyword);
    });
  }, [
    employeeCourses,
    courseSearchTerm,
    courseFilter,
    courseProgressMap,
    selectedCategoryGroup,
  ]);

  const filteredEmployeeCategoryGroups = useMemo<CourseCategoryGroup[]>(() => {
    const groupMap = new Map<string, CourseItem[]>();

    filteredEmployeeCourses.forEach((course) => {
      const categoryKey = course.category_id
        ? `id-${course.category_id}`
        : `name-${course.category || "未分類"}`;

      const existingCourses = groupMap.get(categoryKey) || [];
      groupMap.set(categoryKey, [...existingCourses, course]);
    });

    return Array.from(groupMap.entries()).map(([categoryKey, groupedCourses]) => {
      const firstCourse = groupedCourses[0];

      const totalProgress = groupedCourses.reduce((sum, course) => {
        return sum + (courseProgressMap[course.id]?.final_progress || 0);
      }, 0);

      const averageProgress =
        groupedCourses.length > 0
          ? Math.round(totalProgress / groupedCourses.length)
          : 0;

      const completedCount = groupedCourses.filter(
        (course) => (courseProgressMap[course.id]?.final_progress || 0) >= 100
      ).length;

      const inProgressCount = groupedCourses.filter((course) => {
        const progress = courseProgressMap[course.id]?.final_progress || 0;
        return progress > 0 && progress < 100;
      }).length;

      return {
        categoryKey,
        categoryName: firstCourse.category || "未分類",
        courses: groupedCourses,
        thumbnailCourse: firstCourse,
        averageProgress,
        completedCount,
        inProgressCount,
      };
    });
  }, [filteredEmployeeCourses, courseProgressMap]);

  const selectedCategoryCourses = useMemo(() => {
    if (!selectedCategoryGroup) {
      return [];
    }

    return selectedCategoryGroup.courses.filter((course) => {
      const keyword = courseSearchTerm.trim().toLowerCase();

      if (!keyword) {
        return true;
      }

      return course.name.toLowerCase().includes(keyword);
    });
  }, [selectedCategoryGroup, courseSearchTerm]);

  useEffect(() => {
    const savedCategoryKey = localStorage.getItem("employeeSelectedCategoryKey");

    if (
      savedCategoryKey &&
      !selectedCategoryGroup &&
      filteredEmployeeCategoryGroups.length > 0
    ) {
      const matchedGroup = filteredEmployeeCategoryGroups.find(
        (group) => group.categoryKey === savedCategoryKey
      );

      if (matchedGroup) {
        setSelectedCategoryGroup(matchedGroup);
      }

      localStorage.removeItem("employeeSelectedCategoryKey");
    }
  }, [selectedCategoryGroup, filteredEmployeeCategoryGroups]);

  const deadlineCourses = useMemo(() => {
    return [...filteredEmployeeCourses]
      .filter((course) => course.end_date && course.end_date.trim() !== "")
      .sort((a, b) => {
        const dateA = new Date(a.end_date || "").getTime();
        const dateB = new Date(b.end_date || "").getTime();
        return dateA - dateB;
      });
  }, [filteredEmployeeCourses]);

  const formatDeadlineDate = (dateText?: string | null) => {
    if (!dateText) return "締切日未設定";

    const date = new Date(dateText);

    if (Number.isNaN(date.getTime())) {
      return "締切日未設定";
    }

    return `${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日`;
  };

  const handleLogout = () => {
    sessionStorage.removeItem("loggedInUser");
    navigate("/login");
  };

  const handleOpenCourse = (course: CourseItem) => {
    const matchedGroup = filteredEmployeeCategoryGroups.find((group) =>
      group.courses.some((item) => item.id === course.id)
    );

    if (matchedGroup) {
      setSelectedCategoryGroup(matchedGroup);
      setCourseSearchTerm(course.name);
    }
  };

  const getCourseImageStyle = (course: CourseItem): React.CSSProperties => {
    const hasUploadedThumbnail =
      course.thumbnail && course.thumbnail.trim() !== "";

    const courseText = `${course.category} ${course.name}`.toLowerCase();

    let defaultImageUrl =
      "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?auto=format&fit=crop&w=900&q=80";

    if (
      courseText.includes("hr") ||
      courseText.includes("人事") ||
      courseText.includes("diversity")
    ) {
      defaultImageUrl =
        "https://images.unsplash.com/photo-1551434678-e076c223a692?auto=format&fit=crop&w=900&q=80";
    }

    if (
      courseText.includes("aws") ||
      courseText.includes("cloud") ||
      courseText.includes("python") ||
      courseText.includes("system")
    ) {
      defaultImageUrl =
        "https://images.unsplash.com/photo-1519389950473-47ba0277781c?auto=format&fit=crop&w=900&q=80";
    }

    const imageUrl = hasUploadedThumbnail ? course.thumbnail : defaultImageUrl;

    return {
      height: "180px",
      padding: "16px",
      position: "relative",
      backgroundImage: `linear-gradient(120deg, rgba(15,23,42,0.62), rgba(15,23,42,0.28)), url(${imageUrl})`,
      backgroundSize: "cover",
      backgroundPosition: "center",
    };
  };

  const displayName = loggedInUser?.username || "社員ユーザー";
  const displayDepartment = loggedInUser?.department || "社員";
  const totalUnreadCount = Object.values(chatUnreadCounts).reduce(
    (total, count) => total + count,
    0
  );


  const lastLearningCourse = useMemo(() => {
  if (!loggedInUser?.id) return null;

  const savedCourseId = Number(
    localStorage.getItem(
      `employeeLastLearningCourseId_${loggedInUser.id}`
    )
  );

  if (!savedCourseId) return null;

  return employeeCourses.find(
    (course) => course.id === savedCourseId
  ) || null;
}, [employeeCourses, loggedInUser]);



  if (loading) {
    return <div style={loadingPageStyle}>読み込み中...</div>;
  }

  return (
    <div style={pageStyle}>
      <aside style={sidebarStyle}>
        <div>
          <div style={logoAreaStyle}>
            <div style={logoIconStyle}>🎓</div>
            <div style={logoTextStyle}>LearnHub</div>
          </div>

          <div style={menuSectionTitleStyle}>LEARNING</div>

          <nav style={navStyle}>
            <button
  type="button"
  onClick={() => navigate("/employee-dashboard")}
  style={navItemStyle}
>
  ▦ ダッシュボード
</button>

            <button type="button" style={activeNavItemStyle}>
              📖 マイコース
            </button>

            <button
              type="button"
              onClick={() => {
                localStorage.setItem("employeeDashboardActiveMenu", "tests");
                navigate("/employee-dashboard");
              }}
              style={navItemStyle}
            >
              📝 テスト
            </button>

            

            <button
              type="button"
              onClick={() => {
                localStorage.setItem("employeeDashboardActiveMenu", "notifications");
                navigate("/employee-dashboard");
              }}
              style={navItemStyle}
            >
              🔔 通知
            </button>

            <button
              type="button"
              onClick={() => navigate("/employee-chat")}
              style={chatNavItemStyle}
            >
              <span>💬 チャット</span>
              {totalUnreadCount > 0 && (
                <span style={chatNavBadgeStyle}>{totalUnreadCount}</span>
              )}
            </button>

            <button
              type="button"
              onClick={() => navigate("/employee-settings")}
              style={navItemStyle}
            >
              ⚙️ 設定
            </button>
          </nav>
        </div>

        <div style={profileBottomAreaStyle}>
          <div style={profileAreaStyle}>
            <div style={avatarStyle}>
              {employeeProfileImage ? (
                <img
                  src={employeeProfileImage}
                  alt="プロフィール"
                  style={avatarImageStyle}
                />
              ) : (
                displayName.charAt(0).toUpperCase()
              )}
            </div>

            <div>
              <div style={profileNameStyle}>{displayName}</div>
              <div style={profileDeptStyle}>{displayDepartment}</div>
            </div>

            <button
              type="button"
              onClick={() => navigate("/employee-settings")}
              style={settingButtonStyle}
            >
              ⚙
            </button>
          </div>

          <button
            type="button"
            onClick={handleLogout}
            style={sidebarLogoutButtonStyle}
          >
            ログアウト
          </button>
        </div>
      </aside>

      <main style={mainStyle}>
        <header style={topHeaderStyle}>
          <div>
            <h1 style={pageTitleStyle}>マイコース</h1>
            <p style={pageSubtitleStyle}>受講可能なコースを確認できます</p>
          </div>

          <div style={topRightStyle}>
            <div style={searchBoxStyle}>
              <span style={{ color: "#64748b" }}>🔍</span>
              <input
                type="text"
                value={courseSearchTerm}
                onChange={(e) => setCourseSearchTerm(e.target.value)}
                placeholder={
                  selectedCategoryGroup
                    ? "コース名で検索"
                    : "カテゴリー名で検索"
                }
                style={searchInputStyle}
              />
            </div>
          </div>
        </header>

        <div style={contentGridStyle}>
          <section>
            <div style={sectionHeaderStyle}>
              <h2 style={sectionTitleStyle}>
                {selectedCategoryGroup ? "コース一覧" : "カテゴリー一覧"}
              </h2>
            </div>

            <div style={courseFilterTabsStyle}>
              <button
                type="button"
                onClick={() => setCourseFilter("all")}
                style={courseFilter === "all" ? activeFilterTabStyle : filterTabStyle}
              >
                すべて
              </button>

              <button
                type="button"
                onClick={() => setCourseFilter("assigned")}
                style={courseFilter === "assigned" ? activeFilterTabStyle : filterTabStyle}
              >
                割り当て済み
              </button>

              <button
                type="button"
                onClick={() => setCourseFilter("optional")}
                style={courseFilter === "optional" ? activeFilterTabStyle : filterTabStyle}
              >
                任意
              </button>

              <button
                type="button"
                onClick={() => setCourseFilter("in_progress")}
                style={courseFilter === "in_progress" ? activeFilterTabStyle : filterTabStyle}
              >
                進行中
              </button>

              <button
                type="button"
                onClick={() => setCourseFilter("completed")}
                style={courseFilter === "completed" ? activeFilterTabStyle : filterTabStyle}
              >
                完了
              </button>
            </div>


            {!selectedCategoryGroup && lastLearningCourse && (
  <div
    style={{
      marginBottom: "20px",
      backgroundColor: "#ecfeff",
      border: "1px solid #99f6e4",
      borderRadius: "16px",
      padding: "20px",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
    }}
  >
    <div>
      <div
        style={{
          fontSize: "13px",
          color: "#0f766e",
          fontWeight: 800,
        }}
      >
        前回の学習
      </div>

      <div
        style={{
          fontSize: "20px",
          fontWeight: 900,
          marginTop: "6px",
        }}
      >
        {lastLearningCourse.name}
      </div>
    </div>

    <button
      type="button"
      onClick={() => {
        const matchedGroup =
          filteredEmployeeCategoryGroups.find((group) =>
            group.courses.some(
              (item) => item.id === lastLearningCourse.id
            )
          );

        if (matchedGroup) {
          localStorage.setItem(
            "employeeSelectedCategoryKey",
            matchedGroup.categoryKey
          );
        }

        navigate(`/employee-courses/${lastLearningCourse.id}`);
      }}
      style={courseButtonStyle}
    >
      学習を続ける
    </button>
  </div>
)}

            <div style={courseGridStyle}>
              {!selectedCategoryGroup && filteredEmployeeCategoryGroups.length === 0 && (
                <div style={emptyCourseStyle}>
                  現在、あなたに割り当てられているコースはありません
                </div>
              )}

              {selectedCategoryGroup && (
                <div style={{ gridColumn: "1 / -1", marginBottom: "10px" }}>
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedCategoryGroup(null);
                      setCourseSearchTerm("");
                    }}
                    style={filterTabStyle}
                  >
                    ← カテゴリー一覧へ戻る
                  </button>

                  <h3 style={{ marginTop: "18px", fontSize: "22px", fontWeight: 900 }}>
                    {selectedCategoryGroup.categoryName} のコース一覧
                  </h3>
                </div>
              )}

              {!selectedCategoryGroup &&
                filteredEmployeeCategoryGroups.map((group) => {
                  const firstCourse = group.thumbnailCourse;

                  return (
                    <div key={group.categoryKey} style={courseCardStyle}>
                      <div style={getCourseImageStyle(firstCourse)}>
                        <span style={courseTagStyle}>{group.categoryName}</span>
                        <span style={requiredTagStyle}>{group.courses.length} コース</span>
                        <span style={timeTagStyle}>平均 {group.averageProgress}%</span>
                      </div>

                      <div style={courseBodyStyle}>
                        <h3 style={courseTitleStyle}>{group.categoryName}</h3>

                        <p style={courseDescStyle}>
                          このカテゴリーには {group.courses.length} 件のコースがあります。
                        </p>

                        <div style={courseStatusRowStyle}>
                          <span>完了：{group.completedCount}件</span>
                          <span style={{ color: "#0f766e" }}>
                            進行中：{group.inProgressCount}件
                          </span>
                        </div>

                        <div style={progressBarBgStyle}>
                          <div
                            style={{
                              ...progressBarStyle,
                              width: `${group.averageProgress}%`,
                            }}
                          />
                        </div>

                        <div style={progressTextStyle}>
                          平均進捗 {group.averageProgress}% 完了
                        </div>

                        <button
                          type="button"
                          onClick={() => {
                            setSelectedCategoryGroup(group);
                            setCourseSearchTerm("");
                          }}
                          style={courseButtonStyle}
                        >
                          このカテゴリーを見る
                        </button>
                      </div>
                    </div>
                  );
                })}

              {selectedCategoryGroup &&
                selectedCategoryCourses.map((course) => {
                  const courseProgress = Math.round(
                    courseProgressMap[course.id]?.final_progress || 0
                  );

                  const courseStatus =
                    courseProgress >= 100
                      ? "完了"
                      : courseProgress > 0
                      ? "進行中"
                      : "未開始";

                  return (
                    <div key={course.id} style={courseCardStyle}>
                      <div style={getCourseImageStyle(course)}>
                        <span style={courseTagStyle}>{course.category || "コース"}</span>
                        <span style={requiredTagStyle}>公開</span>
                        <span style={timeTagStyle}>{courseStatus}</span>
                      </div>

                      <div style={courseBodyStyle}>
                        <h3 style={courseTitleStyle}>{course.name}</h3>

                        <p style={courseDescStyle}>講師：{course.instructor || "-"}</p>

                        <div style={courseStatusRowStyle}>
                          <span>状態：{courseStatus}</span>
                          <span style={{ color: "#0f766e" }}>受講可能</span>
                        </div>

                        <div style={progressBarBgStyle}>
                          <div
                            style={{
                              ...progressBarStyle,
                              width: `${courseProgress}%`,
                            }}
                          />
                        </div>

                        <div style={progressTextStyle}>{courseProgress}% 完了</div>

                        <button
                          type="button"
                          onClick={() => {
                            localStorage.setItem(
                              "employeeSelectedCategoryKey",
                              selectedCategoryGroup?.categoryKey || ""
                            );
                            navigate(`/employee-courses/${course.id}`);
                          }}
                          style={courseButtonStyle}
                        >
                          {courseProgress > 0 ? "学習を続ける" : "学習を開始する"}
                        </button>
                      </div>
                    </div>
                  );
                })}
            </div>
          </section>

          <aside>
            <h2 style={sectionTitleStyle}>締切予定</h2>

            <div style={deadlineListStyle}>
              {deadlineCourses.length === 0 && (
                <div style={emptyCourseStyle}>締切予定のコースはありません</div>
              )}

              {deadlineCourses.slice(0, 4).map((course, index) => (
                <div key={course.id} style={deadlineCardStyle}>
                  <div
                    style={
                      index % 2 === 0
                        ? deadlineThumbOneStyle
                        : deadlineThumbTwoStyle
                    }
                  />

                  <div style={{ flex: 1 }}>
                    <div style={deadlineBadgeStyle}>{course.category || "コース"}</div>

                    <div style={deadlineTextStyle}>{course.name}</div>

                    <div style={deadlineDateTextStyle}>
                      締切：{formatDeadlineDate(course.end_date)}
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => handleOpenCourse(course)}
                    style={deadlineArrowButtonStyle}
                  >
                    →
                  </button>
                </div>
              ))}
            </div>
          </aside>
        </div>
      </main>
    </div>
  );
}

const loadingPageStyle: React.CSSProperties = {
  minHeight: "100vh",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  fontSize: "22px",
  fontWeight: 700,
  backgroundColor: "#f8fafc",
};

const pageStyle: React.CSSProperties = {
  minHeight: "100vh",
  display: "flex",
  backgroundColor: "#f8fafc",
  color: "#0f172a",
};

const sidebarStyle: React.CSSProperties = {
  width: "290px",
  minHeight: "100vh",
  backgroundColor: "#14213d",
  color: "#ffffff",
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  flexShrink: 0,
};

const logoAreaStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "14px",
  padding: "22px",
  borderBottom: "1px solid rgba(255,255,255,0.12)",
};

const logoIconStyle: React.CSSProperties = {
  width: "46px",
  height: "46px",
  borderRadius: "14px",
  backgroundColor: "#2dd4bf",
  color: "#0f172a",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "22px",
};

const logoTextStyle: React.CSSProperties = {
  fontSize: "20px",
  fontWeight: 800,
};

const menuSectionTitleStyle: React.CSSProperties = {
  padding: "22px 28px 10px",
  color: "#94a3b8",
  fontSize: "13px",
  fontWeight: 800,
};

const navStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "8px",
  padding: "0 12px",
};

const navItemStyle: React.CSSProperties = {
  width: "100%",
  border: "none",
  backgroundColor: "transparent",
  color: "#cbd5e1",
  textAlign: "left",
  padding: "14px 16px",
  borderRadius: "14px",
  fontSize: "16px",
  fontWeight: 700,
  cursor: "pointer",
};

const activeNavItemStyle: React.CSSProperties = {
  ...navItemStyle,
  backgroundColor: "#1e3a68",
  color: "#ffffff",
};

const profileBottomAreaStyle: React.CSSProperties = {
  borderTop: "1px solid rgba(255,255,255,0.12)",
  padding: "18px",
};

const profileAreaStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "12px",
};

const avatarStyle: React.CSSProperties = {
  width: "46px",
  height: "46px",
  borderRadius: "50%",
  backgroundColor: "#dbeafe",
  color: "#1e3a8a",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontWeight: 800,
  overflow: "hidden",
  flexShrink: 0,
};

const avatarImageStyle: React.CSSProperties = {
  width: "100%",
  height: "100%",
  objectFit: "cover",
};

const profileNameStyle: React.CSSProperties = {
  fontSize: "15px",
  fontWeight: 800,
};

const profileDeptStyle: React.CSSProperties = {
  fontSize: "13px",
  color: "#93c5fd",
};

const settingButtonStyle: React.CSSProperties = {
  marginLeft: "auto",
  color: "#cbd5e1",
  border: "none",
  backgroundColor: "transparent",
  fontSize: "18px",
  cursor: "pointer",
};

const sidebarLogoutButtonStyle: React.CSSProperties = {
  width: "100%",
  marginTop: "14px",
  border: "none",
  backgroundColor: "rgba(239, 68, 68, 0.18)",
  color: "#fecaca",
  padding: "12px 14px",
  borderRadius: "12px",
  fontWeight: 900,
  cursor: "pointer",
};

const mainStyle: React.CSSProperties = {
  flex: 1,
  padding: "22px 28px 40px",
};

const topHeaderStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: "28px",
};

const pageTitleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: "26px",
  fontWeight: 800,
};

const pageSubtitleStyle: React.CSSProperties = {
  margin: "4px 0 0",
  color: "#64748b",
  fontSize: "16px",
};

const topRightStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "18px",
};

const searchBoxStyle: React.CSSProperties = {
  width: "340px",
  backgroundColor: "#ffffff",
  border: "1px solid #e2e8f0",
  borderRadius: "14px",
  padding: "12px 16px",
  color: "#64748b",
  fontSize: "15px",
  display: "flex",
  alignItems: "center",
  gap: "8px",
};

const searchInputStyle: React.CSSProperties = {
  width: "100%",
  border: "none",
  outline: "none",
  fontSize: "15px",
  color: "#0f172a",
  backgroundColor: "transparent",
};

const contentGridStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "1fr 320px",
  gap: "30px",
};

const sectionHeaderStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: "18px",
};

const sectionTitleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: "22px",
  fontWeight: 900,
};

const courseGridStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  gap: "20px",
};

const courseCardStyle: React.CSSProperties = {
  backgroundColor: "#ffffff",
  border: "1px solid #e2e8f0",
  borderRadius: "16px",
  overflow: "hidden",
};

const emptyCourseStyle: React.CSSProperties = {
  padding: "40px",
  backgroundColor: "#ffffff",
  borderRadius: "16px",
  border: "1px dashed #cbd5e1",
  textAlign: "center",
  color: "#64748b",
  fontWeight: 700,
};

const courseTagStyle: React.CSSProperties = {
  backgroundColor: "#ffffff",
  color: "#0f172a",
  padding: "7px 14px",
  borderRadius: "999px",
  fontSize: "13px",
  fontWeight: 800,
};

const requiredTagStyle: React.CSSProperties = {
  position: "absolute",
  top: "18px",
  right: "18px",
  color: "#ffffff",
  fontWeight: 800,
};

const timeTagStyle: React.CSSProperties = {
  position: "absolute",
  left: "18px",
  bottom: "18px",
  color: "#ffffff",
  fontWeight: 800,
};

const courseBodyStyle: React.CSSProperties = {
  padding: "18px",
};

const courseTitleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: "18px",
  fontWeight: 900,
};

const courseDescStyle: React.CSSProperties = {
  color: "#64748b",
  lineHeight: 1.6,
  minHeight: "52px",
};

const courseStatusRowStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  fontSize: "14px",
  fontWeight: 800,
  color: "#64748b",
  marginBottom: "12px",
};

const progressBarBgStyle: React.CSSProperties = {
  height: "9px",
  borderRadius: "999px",
  backgroundColor: "#dbeafe",
  overflow: "hidden",
};

const progressBarStyle: React.CSSProperties = {
  height: "100%",
  backgroundColor: "#2563eb",
};

const progressTextStyle: React.CSSProperties = {
  marginTop: "8px",
  color: "#64748b",
  fontSize: "14px",
};

const courseButtonStyle: React.CSSProperties = {
  width: "100%",
  marginTop: "12px",
  border: "none",
  backgroundColor: "#0f766e",
  color: "#ffffff",
  padding: "13px",
  borderRadius: "12px",
  fontWeight: 800,
  cursor: "pointer",
};

const deadlineListStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "14px",
  marginTop: "20px",
};

const deadlineCardStyle: React.CSSProperties = {
  backgroundColor: "#ffffff",
  border: "1px solid #e2e8f0",
  borderRadius: "16px",
  padding: "16px",
  display: "flex",
  alignItems: "center",
  gap: "14px",
};

const deadlineThumbOneStyle: React.CSSProperties = {
  width: "64px",
  height: "64px",
  borderRadius: "14px",
  background: "linear-gradient(45deg, #1e3a8a, #14b8a6)",
};

const deadlineThumbTwoStyle: React.CSSProperties = {
  ...deadlineThumbOneStyle,
  background: "linear-gradient(45deg, #94a3b8, #e2e8f0)",
};

const deadlineBadgeStyle: React.CSSProperties = {
  display: "inline-block",
  backgroundColor: "#dbeafe",
  color: "#2563eb",
  padding: "5px 12px",
  borderRadius: "999px",
  fontSize: "13px",
  fontWeight: 800,
};

const deadlineTextStyle: React.CSSProperties = {
  marginTop: "6px",
  color: "#64748b",
  fontSize: "13px",
};

const deadlineDateTextStyle: React.CSSProperties = {
  marginTop: "6px",
  color: "#ef4444",
  fontSize: "12px",
  fontWeight: 800,
};

const deadlineArrowButtonStyle: React.CSSProperties = {
  border: "none",
  backgroundColor: "transparent",
  color: "#0f766e",
  fontSize: "22px",
  fontWeight: 900,
  cursor: "pointer",
};

const courseFilterTabsStyle: React.CSSProperties = {
  display: "flex",
  gap: "12px",
  marginBottom: "20px",
  flexWrap: "wrap",
};

const filterTabStyle: React.CSSProperties = {
  border: "none",
  backgroundColor: "#ffffff",
  color: "#0f172a",
  padding: "12px 18px",
  borderRadius: "14px",
  fontWeight: 800,
  cursor: "pointer",
  boxShadow: "0 4px 14px rgba(15, 23, 42, 0.05)",
};

const activeFilterTabStyle: React.CSSProperties = {
  ...filterTabStyle,
  backgroundColor: "#0f766e",
  color: "#ffffff",
};

const chatNavItemStyle: React.CSSProperties = {
  ...navItemStyle,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
};

const chatNavBadgeStyle: React.CSSProperties = {
  minWidth: "22px",
  height: "22px",
  padding: "0 7px",
  borderRadius: "999px",
  backgroundColor: "#ef4444",
  color: "#ffffff",
  fontSize: "12px",
  fontWeight: 900,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

export default EmployeeCoursesPage;